import { recordAction } from "@/lib/actionLedger";
import { db } from "@/lib/firebase";
import { Page, parsePage } from "@/schemas/firestore/page.schema";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

const isDraftPage = (p: Page) => p.source === "wordpress" && !p.url;

// Define the Page slice of the store
export interface PageSlice {
  // State
  pages: Page[];
  publishedPages: Page[];
  draftPages: Page[];
  currentPage: Page | null;
  isLoadingPages: boolean;
  pageError: string | null;

  // Actions
  subscribeToPages: (orgId: string, domainId: string | null) => () => void;
  setCurrentPage: (pageId: string | null) => Promise<void>;
  addPage: (orgId: string, domainId: string, url: string) => Promise<string>;
  deletePage: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => Promise<void>;
  unsubscribePages: () => void;

  // Track page subscriptions by domainId
  _pageSubscriptions: Map<string, () => void>;
}

// Create the Page slice
export const createPageSlice: StateCreator<StoreState, [], [], PageSlice> = (
  set,
  get,
) => ({
  // Initial state
  pages: [],
  publishedPages: [],
  draftPages: [],
  currentPage: null,
  isLoadingPages: false,
  pageError: null,
  _pageSubscriptions: new Map(),

  // Subscribe to real-time updates for pages within a domain
  subscribeToPages: (orgId, domainId) => {
    // If we don't have both orgId and domainId, we can't subscribe to pages
    if (!orgId || !domainId) {
      console.log("Pages subscription requires orgId and domainId.");
      return () => {};
    }

    // Check if we already have a subscription for this domain
    const existingUnsubscribe = get()._pageSubscriptions.get(domainId);
    if (existingUnsubscribe) {
      console.log(`Pages subscription already exists for domain ${domainId}`);
      return existingUnsubscribe;
    }

    set((state) => ({
      isLoadingPages: state.pages.length === 0, // Only show loading on initial load
      pageError: null,
    }));

    // Create reference to the pages collection for this domain
    const pagesRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/pages`,
    );
    const q = query(pagesRef, orderBy("created_at", "desc"));

    // Set up real-time listener for pages
    console.log(`Setting up real-time pages listener for domain ${domainId}`);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const pagesList: Page[] = [];

        for (const docSnap of snapshot.docs) {
          try {
            const page = parsePage({
              id: docSnap.id,
              ...docSnap.data(),
              domainId,
            });
            pagesList.push(page);
          } catch (error) {
            // Skip the invalid doc so one bad page doesn't break the whole list
            console.error(`Page validation failed for document ${docSnap.id}:`, {
              error: error instanceof Error ? error.message : error,
              rawData: docSnap.data(),
              domainId,
            });
          }
        }

        console.log(
          `Pages snapshot received with ${snapshot.docs.length} documents for domain ${domainId}`,
        );

        // Merge with pages of other domains
        set((state) => {
          const other = state.pages.filter((p) => p.domainId !== domainId);
          const merged = [...other, ...pagesList];
          return {
            pages: merged,
            publishedPages: merged.filter((p) => !isDraftPage(p)),
            draftPages: merged.filter((p) => isDraftPage(p)),
            isLoadingPages: false,
          };
        });

        // Update current page object if needed
        const currentPageId = get().userData?.currentPageId;
        if (currentPageId) {
          const currentPage = pagesList.find((p) => p.id === currentPageId);
          if (currentPage) {
            set({ currentPage });
          }
        }
      },
      (error) => {
        console.error(
          `Error in pages subscription for domain ${domainId}:`,
          error,
        );
        set({
          pageError: `Failed to subscribe to pages for domain ${domainId}`,
          isLoadingPages: false,
        });
      },
    );

    // Track this subscription by domainId
    const wrappedUnsubscribe = () => {
      unsubscribe();
      get()._pageSubscriptions.delete(domainId);
      console.log(`Unsubscribed from pages for domain ${domainId}`);
    };

    set((state) => {
      const newSubscriptions = new Map(state._pageSubscriptions);
      newSubscriptions.set(domainId, wrappedUnsubscribe);
      return { _pageSubscriptions: newSubscriptions };
    });

    return wrappedUnsubscribe;
  },

  // Set the current page
  setCurrentPage: async (pageId) => {
    const domainId = get().userData?.currentDomainId;
    if (!domainId) {
      set({ currentPage: null, pageError: "No current domain ID found." });
      return;
    }

    if (!pageId) {
      set({ currentPage: null });
      get().subscribeToJobs(domainId);
      return;
    }

    const page = get().pages.find((p) => p.id === pageId);
    if (page) {
      set({ currentPage: page });
      get().subscribeToJobs(domainId);
    } else {
      set({ currentPage: null, pageError: "Page not found." });
      get().subscribeToJobs(domainId);
    }
  },

  // Add a new page to the current domain
  addPage: async (orgId: string, domainId: string, url: string) => {
    if (!orgId || !domainId) {
      set({ pageError: "Organization ID and Domain ID are required to add a page." });
      throw new Error("Organization ID and Domain ID are required to add a page.");
    }
    try {
      const pagesRef = collection(
        db,
        `organizations/${orgId}/domains/${domainId}/pages`,
      );
      // `created_by` is pinned to request.auth.uid by firestore.rules, so this
      // cannot attribute the write to anyone else -- and the write is rejected
      // outright if the field is missing.
      const newPageDoc = await addDoc(pagesRef, {
        url: url,
        created_at: serverTimestamp(),
        created_by: get().currentUser?.uid ?? "",
      });
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "page.created",
        targetPath: newPageDoc.path,
        domainId,
        summary: url,
      });
      return newPageDoc.id; // Return the new page ID
    } catch (error) {
      console.error("Error adding page:", error);
      set({ pageError: "Failed to add page" });
      throw new Error("Failed to add page");
    }
  },

  // Delete a page and its subcollections (jobs)
  deletePage: async (orgId, domainId, pageId) => {
    if (!orgId || !domainId || !pageId) return;

    set({ isLoadingPages: true }); // Indicate loading
    try {
      const pageRef = doc(
        db,
        `organizations/${orgId}/domains/${domainId}/pages`,
        pageId,
      );
      const jobsRef = collection(pageRef, "heuristic_jobs");

      const batch = writeBatch(db);

      // Delete jobs within the page
      const jobsQuery = query(jobsRef);
      const jobsDocs = await getDocs(jobsQuery);
      jobsDocs.forEach((jobDoc) => {
        batch.delete(jobDoc.ref);
      });

      // Delete the page document itself
      batch.delete(pageRef);

      // Recorded before the batch commits: once the document is gone there is
      // no `created_by` left to read, and the deleter would be unattributable.
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "page.deleted",
        targetPath: pageRef.path,
        domainId,
        summary: get().pages.find((p: Page) => p.id === pageId)?.url ?? pageId,
      });

      await batch.commit();

      // Optimistically remove from local state
      set((state: StoreState) => ({
        pages: state.pages.filter((p: Page) => p.id !== pageId),
        currentPage:
          state.currentPage?.id === pageId ? null : state.currentPage,
        isLoadingPages: false,
      }));

      // If the deleted page was the current one, update user doc
      const currentUser = get().currentUser;
      if (currentUser && get().userData?.currentPageId === pageId) {
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, { currentPageId: "" });
        // Also trigger updates in other slices
        get().setCurrentPage(null); // This will trigger job updates via its logic
      }
    } catch (error) {
      console.error("Error deleting page:", error);
      set({ pageError: "Failed to delete page", isLoadingPages: false });
    }
  },

  // Manually unsubscribe from all pages listeners
  unsubscribePages: () => {
    const subscriptions = get()._pageSubscriptions;
    console.log(`Unsubscribing from ${subscriptions.size} page subscriptions`);

    // Unsubscribe from all domain page listeners
    subscriptions.forEach((unsubscribe, domainId) => {
      console.log(`Unsubscribing from pages for domain ${domainId}`);
      unsubscribe();
    });

    // Clear state
    set({
      pages: [],
      currentPage: null,
      _pageSubscriptions: new Map(),
    });
  },
});
